import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLatest } from '../../hooks/useLatest';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiPlus, FiEdit, FiTrash2, FiCalendar, FiClock, FiMapPin, FiFilter, FiSearch, FiLink, FiVideo, FiRefreshCw, FiPlay, FiFileText } from 'react-icons/fi';
import { meetingsAPI, usersAPI, zoomAPI, b2bAgenciesAPI, settingsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import StatusBadge from '../../components/Common/StatusBadge';
import Modal from '../../components/Common/Modal';
import { toast } from 'react-toastify';
import { canMarkMeetingCompleted, meetingCompletedBlockedMessage } from '../../utils/meetingCompletion';
import { getTodayDateInputMin, isMeetingStartInFuture } from '../../utils/futureDateTime';
import { resolveDefaultTimezoneFromList } from '../../utils/browserTimezone';
import { useCountry } from '../../context/CountryContext';
import AuditTimeline from '../../components/Common/AuditTimeline';
import ColumnCustomizer, { useColumnPreferences } from '../../components/Common/ColumnCustomizer';
import { UnifiedFilterHeaderCell } from '../../components/Common/UnifiedTableFilters';
import {
  runTableFilters,
  hasActiveClientTableFilters,
  CLIENT_SIDE_TABLE_FILTER_FETCH_LIMIT
} from '../../utils/tableFilters';

const MEETING_LIST_PAGE_SIZE = 20;

const MEETING_TYPES = ['Student Counseling', 'Agency Partnership', 'Course Consultation', 'Online Meeting', 'Office Visit'];
const MEETING_MODES = ['Online', 'Offline'];
const MEETING_STATUSES = ['Scheduled', 'Completed', 'Cancelled', 'Rescheduled'];
const TIME_ZONES = ['Asia/Colombo', 'Asia/Dubai', 'Europe/Paris', 'Europe/London', 'Asia/Kolkata', 'UTC'];

const MeetingList = () => {
  const { t } = useTranslation();
  const { user, canPerformAction } = useAuth();
  const { getConfig, activeCountry } = useCountry();
  const countryConfig = getConfig();
  const [searchParams] = useSearchParams();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [filters, setFilters] = useState({ search: '', meeting_type: '', status: '', date_from: '', date_to: '' });
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 0 });
  const [showModal, setShowModal] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState(null);
  const [users, setUsers] = useState([]);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const [zoomConfigured, setZoomConfigured] = useState(false);
  const [zoomGenerating, setZoomGenerating] = useState(null);
  const [zoomGeneratingModal, setZoomGeneratingModal] = useState(false);
  const [zoomGeneratingReschedule, setZoomGeneratingReschedule] = useState(false);
  const [zoomSyncing, setZoomSyncing] = useState(null);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleMeeting, setRescheduleMeeting] = useState(null);
  const [rescheduleForm, setRescheduleForm] = useState(null);
  const [b2bPartners, setB2bPartners] = useState([]);
  const [initialMeetingSlot, setInitialMeetingSlot] = useState(null);
  const [meetingHistory, setMeetingHistory] = useState([]);
  const [meetingHistoryLoading, setMeetingHistoryLoading] = useState(false);
  const [tableFilters, setTableFilters] = useState({ globalSearch: '', columns: {} });

  const meetingTypeLabel = (v) => ({
    'Student Counseling': t('meetings.types.studentCounseling'),
    'Agency Partnership': t('meetings.types.agencyPartnership'),
    'Course Consultation': t('meetings.types.courseConsultation'),
    'Online Meeting': t('meetings.types.onlineMeeting'),
    'Office Visit': t('meetings.types.officeVisit'),
  })[v] || v;

  const meetingModeLabel = (v) => ({
    'Online': t('meetings.modes.online'),
    'Offline': t('meetings.modes.offline'),
  })[v] || v;

  const meetingStatusLabel = (v) => ({
    'Scheduled': t('meetings.statuses.scheduled'),
    'Completed': t('meetings.statuses.completed'),
    'Cancelled': t('meetings.statuses.cancelled'),
    'Rescheduled': t('meetings.statuses.rescheduled'),
  })[v] || v;

  const meetingColumns = useMemo(() => [
    { key: 'title', label: t('meetings.tableTitle') },
    { key: 'meeting_type', label: t('meetings.tableType') },
    { key: 'date_time', label: t('meetings.tableDateTime') },
    { key: 'mode', label: t('meetings.tableMode') },
    { key: 'location_link', label: t('meetings.tableLocationLink') },
    { key: 'created_by', label: t('meetings.tableCreatedBy') },
    { key: 'status', label: t('meetings.tableStatus') },
    { key: 'lead_name', label: t('meetings.leadName', 'Agency/Lead'), defaultVisible: false },
    { key: 'student_full_name', label: t('meetings.studentName', 'Student'), defaultVisible: false },
    { key: 'notes', label: t('meetings.notes', 'Notes'), defaultVisible: false },
    { key: 'outcome', label: t('meetings.outcome', 'Outcome'), defaultVisible: false },
    { key: 'next_action', label: t('meetings.nextAction', 'Next Action'), defaultVisible: false },
    { key: 'lead_ref', label: t('meetings.leadRef', 'Lead Ref'), defaultVisible: false },
    { key: 'actions', label: t('common.actions'), locked: true },
  ], [t]);

  const { isVisible, toggleColumn, resetToDefault, columns: mtgColDefs } = useColumnPreferences('meetings', meetingColumns);
  const columnFilterDefs = useMemo(() => {
    const createdByOptions = Array.from(new Set((meetings || []).map((m) => String(m.created_by_name || '')).filter(Boolean)));
    const leadOptions = Array.from(new Set((meetings || []).map((m) => String(m.lead_name || '')).filter(Boolean)));
    return [
      { key: 'title', label: t('meetings.tableTitle'), field: 'title', type: 'text' },
      { key: 'meeting_type', label: t('meetings.tableType'), field: 'meeting_type', type: 'multi', options: MEETING_TYPES.map((v) => ({ value: v, label: meetingTypeLabel(v) })) },
      { key: 'date_time', label: t('meetings.tableDateTime'), field: 'meeting_date', type: 'date' },
      { key: 'mode', label: t('meetings.tableMode'), field: 'mode', type: 'multi', options: MEETING_MODES.map((v) => ({ value: v, label: meetingModeLabel(v) })) },
      { key: 'location_link', label: t('meetings.tableLocationLink'), field: 'meeting_link', type: 'text' },
      { key: 'created_by', label: t('meetings.tableCreatedBy'), field: 'created_by_name', type: 'multi', options: createdByOptions.map((v) => ({ value: v, label: v })) },
      { key: 'status', label: t('meetings.tableStatus'), field: 'status', type: 'multi', options: MEETING_STATUSES.map((v) => ({ value: v, label: meetingStatusLabel(v) })) },
      { key: 'lead_name', label: t('meetings.leadName', 'Agency/Lead'), field: 'lead_name', type: 'multi', options: leadOptions.map((v) => ({ value: v, label: v })) },
      { key: 'student_full_name', label: t('meetings.studentName', 'Student'), field: 'student_full_name', type: 'text' },
      { key: 'notes', label: t('meetings.notes', 'Notes'), field: 'notes', type: 'text' },
      { key: 'outcome', label: t('meetings.outcome', 'Outcome'), field: 'outcome', type: 'text' },
      { key: 'next_action', label: t('meetings.nextAction', 'Next Action'), field: 'next_action', type: 'text' },
      { key: 'lead_ref', label: t('meetings.leadRef', 'Lead Ref'), field: 'lead_ref', type: 'text' }
    ];
  }, [meetings, t]);
  const columnFilterDefsRef = useLatest(columnFilterDefs);
  const columnFilterMap = useMemo(() => Object.fromEntries(columnFilterDefs.map((d) => [d.key, d])), [columnFilterDefs]);
  const updateColumnFilters = useCallback((updater) => setTableFilters((prev) => ({ ...prev, columns: updater(prev.columns || {}) })), []);
  const filteredMeetings = useMemo(
    () => runTableFilters(meetings, tableFilters, tableFilters, ['title', 'lead_name', 'student_full_name', 'notes', 'lead_ref'], columnFilterDefs),
    [meetings, tableFilters, columnFilterDefs]
  );
  const clientTableFilterMode = useMemo(
    () => hasActiveClientTableFilters(tableFilters, columnFilterDefs),
    [tableFilters, columnFilterDefs]
  );
  const pagedFilteredMeetings = useMemo(() => {
    if (!clientTableFilterMode) return filteredMeetings;
    const start = (pagination.page - 1) * MEETING_LIST_PAGE_SIZE;
    return filteredMeetings.slice(start, start + MEETING_LIST_PAGE_SIZE);
  }, [clientTableFilterMode, filteredMeetings, pagination.page]);
  const listTotalForPaging = clientTableFilterMode ? filteredMeetings.length : pagination.total;
  const effectiveTotalPages = Math.max(
    1,
    Math.ceil(listTotalForPaging / MEETING_LIST_PAGE_SIZE)
  );

  useEffect(() => {
    if (pagination.page > effectiveTotalPages) {
      setPagination((p) => ({ ...p, page: effectiveTotalPages }));
    }
  }, [clientTableFilterMode, filteredMeetings.length, pagination.total, pagination.page, effectiveTotalPages]);

  const leadOptionLabel = useCallback(
    (a) => {
      const ref = (a?.ref_number || '').trim() || (a?.lead_id != null ? `#${a.lead_id}` : '');
      const name = (a?.student_name || '').trim() || (a?.email || '').trim() || t('meetings.unknown');
      return ref ? `${ref} — ${name}` : name;
    },
    [t]
  );

  const emptyForm = {
    title: '', meeting_type: 'Student Counseling', meeting_date: new Date(new Date().toLocaleString('en-US', { timeZone: countryConfig.defaultTimezone })).toISOString().slice(0, 10),
    meeting_time: '10:00', timezone: countryConfig.defaultTimezone, mode: 'Online', location: '', meeting_link: '',
    zoom_meeting_id: '', zoom_join_url: '', zoom_start_url: '', zoom_passcode: '',
    lead_id: '', student_id: '', notes: '', status: 'Scheduled'
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    const statusParam = searchParams.get('status');
    if (statusParam === 'all_pending_overdue') {
      const yesterday = new Date(new Date().toLocaleString('en-US', { timeZone: countryConfig.defaultTimezone }));
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().slice(0, 10);
      setFilters(f => ({ ...f, status: '', date_to: yesterdayStr }));
      setPagination(p => ({ ...p, page: 1 }));
    } else if (statusParam && MEETING_STATUSES.includes(statusParam)) {
      setFilters(f => ({ ...f, status: statusParam }));
      setPagination(p => ({ ...p, page: 1 }));
    }
  }, [searchParams]);

  const loadMeetings = useCallback(async () => {
    setLoading(true);
    try {
      const active = clientTableFilterMode;
      const params = {
        page: active ? 1 : pagination.page,
        limit: active ? CLIENT_SIDE_TABLE_FILTER_FETCH_LIMIT : MEETING_LIST_PAGE_SIZE
      };
      if (filters.search) params.search = filters.search;
      if (filters.meeting_type) params.meeting_type = filters.meeting_type;
      if (filters.status) params.status = filters.status;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const res = await meetingsAPI.getAll(params);
      setMeetings(res.data.meetings || []);
      setPagination(p => ({ ...p, total: res.data.total || 0, pages: res.data.pages || 0 }));
    } catch (err) { toast.error(t('meetings.toasts.loadFailed')); }
    setLoading(false);
  }, [pagination.page, filters, clientTableFilterMode]);

  useEffect(() => { loadMeetings(); }, [loadMeetings]);
  useEffect(() => {
    usersAPI.getAll().then(r => setUsers(r.data.users || r.data || [])).catch(() => {});
    zoomAPI.getStatus().then(r => setZoomConfigured(r.data.configured)).catch(() => {});
    b2bAgenciesAPI.getAll({ limit: 500, page: 1 })
      .then((r) => setB2bPartners(r.data.agencies || r.data.leads || []))
      .catch(() => setB2bPartners([]));
  }, []);

  useEffect(() => {
    const meetingId = editingMeeting?.meeting_id;
    if (!showModal || !meetingId) {
      setMeetingHistory([]);
      setMeetingHistoryLoading(false);
      return;
    }
    setMeetingHistoryLoading(true);
    settingsAPI
      .getAuditLog({ entity_type: 'meeting', entity_id: meetingId, limit: 200, page: 1 })
      .then((r) => setMeetingHistory(r.data || []))
      .catch(() => setMeetingHistory([]))
      .finally(() => setMeetingHistoryLoading(false));
  }, [editingMeeting?.meeting_id, showModal]);

  const formatTimeForInput = (ti) => {
    if (!ti) return '10:00';
    const s = String(ti);
    return s.length >= 5 ? s.slice(0, 5) : s;
  };

  const openRescheduleModal = (meeting) => {
    setRescheduleMeeting(meeting);
    setRescheduleForm({
      meeting_date: meeting.meeting_date?.slice(0, 10) || '',
      meeting_time: formatTimeForInput(meeting.meeting_time),
      timezone: meeting.timezone || resolveDefaultTimezoneFromList(TIME_ZONES),
      mode: meeting.mode || 'Online',
      location: meeting.location || '',
      meeting_link: meeting.meeting_link || '',
      zoom_meeting_id: meeting.zoom_meeting_id || '',
      zoom_join_url: meeting.zoom_join_url || '',
      zoom_start_url: meeting.zoom_start_url || '',
      zoom_passcode: meeting.zoom_passcode || ''
    });
    setShowRescheduleModal(true);
  };

  const closeRescheduleModal = () => {
    setShowRescheduleModal(false);
    setRescheduleMeeting(null);
    setRescheduleForm(null);
  };

  const generateZoomInRescheduleModal = async () => {
    if (!rescheduleForm?.meeting_date || !rescheduleForm?.meeting_time) {
      toast.error(t('meetings.toasts.setDateTimeFirst'));
      return;
    }
    if (!isMeetingStartInFuture(rescheduleForm.meeting_date, rescheduleForm.meeting_time, rescheduleForm.timezone)) {
      toast.error(t('meetings.toasts.mustBeFuture'));
      return;
    }
    if (rescheduleForm.mode !== 'Online') {
      toast.error(t('meetings.toasts.zoomOnlineOnly'));
      return;
    }
    if (!rescheduleMeeting?.meeting_id) return;
    setZoomGeneratingReschedule(true);
    try {
      const startTime = `${rescheduleForm.meeting_date.slice(0, 10)}T${rescheduleForm.meeting_time}:00`;
      const { data } = await zoomAPI.generate({
        topic: rescheduleMeeting.title || 'CRM Meeting',
        start_time: startTime,
        duration: 60,
        timezone: rescheduleForm.timezone || resolveDefaultTimezoneFromList(TIME_ZONES),
        entity_type: 'meeting',
        entity_id: rescheduleMeeting.meeting_id
      });
      setRescheduleForm((f) => ({
        ...f,
        meeting_link: data.join_url || f.meeting_link,
        zoom_meeting_id: data.zoom_meeting_id || '',
        zoom_join_url: data.join_url || '',
        zoom_start_url: data.start_url || '',
        zoom_passcode: data.passcode || ''
      }));
      toast.success(t('meetings.toasts.zoomLinkUpdated'));
    } catch (err) {
      toast.error(err.response?.data?.message || t('meetings.toasts.zoomGenerateFailed'));
    }
    setZoomGeneratingReschedule(false);
  };

  const handleRescheduleSubmit = async () => {
    if (!rescheduleForm || !rescheduleMeeting) return;
    if (!rescheduleForm.meeting_date || !rescheduleForm.meeting_time) {
      toast.error(t('meetings.toasts.dateTimeRequired'));
      return;
    }
    if (rescheduleForm.mode === 'Offline' && !rescheduleForm.location?.trim()) {
      toast.error(t('meetings.toasts.locationRequired'));
      return;
    }
    if (!isMeetingStartInFuture(rescheduleForm.meeting_date, rescheduleForm.meeting_time, rescheduleForm.timezone)) {
      toast.error(t('meetings.toasts.mustBeFuture'));
      return;
    }
    try {
      setUpdatingStatusId(rescheduleMeeting.meeting_id);
      await meetingsAPI.update(rescheduleMeeting.meeting_id, {
        title: rescheduleMeeting.title,
        meeting_type: rescheduleMeeting.meeting_type,
        meeting_date: rescheduleForm.meeting_date,
        meeting_time: rescheduleForm.meeting_time,
        timezone: rescheduleForm.timezone,
        mode: rescheduleForm.mode,
        location: rescheduleForm.mode === 'Offline' ? rescheduleForm.location : '',
        meeting_link: rescheduleForm.mode === 'Online' ? (rescheduleForm.meeting_link || null) : null,
        notes: rescheduleMeeting.notes,
        next_action: rescheduleMeeting.next_action,
        outcome: rescheduleMeeting.outcome,
        status: 'Rescheduled',
        zoom_meeting_id: rescheduleForm.zoom_meeting_id || null,
        zoom_join_url: rescheduleForm.zoom_join_url || null,
        zoom_start_url: rescheduleForm.zoom_start_url || null,
        zoom_passcode: rescheduleForm.zoom_passcode || null
      });
      toast.success(t('meetings.toasts.rescheduled'));
      closeRescheduleModal();
      loadMeetings();
    } catch (err) {
      toast.error(err.response?.data?.message || t('meetings.toasts.rescheduleFailed'));
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleStatusSelectChange = (meeting, newStatus) => {
    if (newStatus === 'Rescheduled') {
      openRescheduleModal(meeting);
      return;
    }
    handleStatusUpdate(meeting, newStatus);
  };

  const generateZoomLink = async (meeting) => {
    if (!meeting.meeting_date || !meeting.meeting_time) {
      toast.error(t('meetings.toasts.setDateTimeFirst')); return;
    }
    setZoomGenerating(meeting.meeting_id);
    try {
      const startTime = `${meeting.meeting_date.slice(0, 10)}T${meeting.meeting_time}:00`;
      const { data } = await zoomAPI.generate({
        topic: meeting.title || 'CRM Meeting',
        start_time: startTime,
        duration: 60,
        timezone: meeting.timezone || resolveDefaultTimezoneFromList(TIME_ZONES),
        entity_type: 'meeting',
        entity_id: meeting.meeting_id
      });
      toast.success(t('meetings.toasts.zoomCreated'));
      loadMeetings();
    } catch (err) {
      toast.error(err.response?.data?.message || t('meetings.toasts.zoomGenerateFailed'));
    }
    setZoomGenerating(null);
  };

  const generateZoomInModal = async () => {
    if (!form.meeting_date || !form.meeting_time) {
      toast.error(t('meetings.toasts.setDateTimeFirst'));
      return;
    }
    if (!isMeetingStartInFuture(form.meeting_date, form.meeting_time, form.timezone)) {
      toast.error(t('meetings.toasts.mustBeFuture'));
      return;
    }
    if (!form.title?.trim()) {
      toast.error(t('meetings.toasts.enterTitle'));
      return;
    }
    if (form.mode !== 'Online') {
      toast.error(t('meetings.toasts.switchToOnline'));
      return;
    }
    setZoomGeneratingModal(true);
    try {
      const startTime = `${form.meeting_date.slice(0, 10)}T${form.meeting_time}:00`;
      const payload = {
        topic: form.title.trim() || 'CRM Meeting',
        start_time: startTime,
        duration: 60,
        timezone: form.timezone || resolveDefaultTimezoneFromList(TIME_ZONES),
        entity_type: 'meeting'
      };
      if (editingMeeting?.meeting_id) {
        payload.entity_id = editingMeeting.meeting_id;
      }
      const { data } = await zoomAPI.generate(payload);
      setForm((f) => ({
        ...f,
        meeting_link: data.join_url || f.meeting_link,
        zoom_meeting_id: data.zoom_meeting_id || '',
        zoom_join_url: data.join_url || '',
        zoom_start_url: data.start_url || '',
        zoom_passcode: data.passcode || ''
      }));
      toast.success(t('meetings.toasts.zoomCreated'));
    } catch (err) {
      toast.error(err.response?.data?.message || t('meetings.toasts.zoomGenerateFailed'));
    }
    setZoomGeneratingModal(false);
  };

  const syncZoomData = async (meeting) => {
    if (!meeting.zoom_meeting_id) return;
    setZoomSyncing(meeting.meeting_id);
    try {
      await zoomAPI.sync({ entity_type: 'meeting', entity_id: meeting.meeting_id });
      toast.success(t('meetings.toasts.zoomSynced'));
      loadMeetings();
    } catch (err) {
      toast.error(err.response?.data?.message || t('meetings.toasts.syncFailed'));
    }
    setZoomSyncing(null);
  };

  const handleSubmit = async () => {
    if (!form.title || !form.meeting_date || !form.meeting_time) {
      toast.error(t('meetings.toasts.fillRequired')); return;
    }
    if (form.mode === 'Offline' && !form.location) {
      toast.error(t('meetings.toasts.locationRequired')); return;
    }
    const mustBeFuture =
      !editingMeeting ||
      form.meeting_date !== initialMeetingSlot?.date ||
      form.meeting_time !== initialMeetingSlot?.time;
    if (mustBeFuture && !isMeetingStartInFuture(form.meeting_date, form.meeting_time, form.timezone)) {
      toast.error(t('meetings.toasts.mustBeFuture'));
      return;
    }
    if (
      editingMeeting &&
      form.status === 'Completed' &&
      editingMeeting.status !== 'Completed' &&
      !canMarkMeetingCompleted(editingMeeting, user?.role, activeCountry)
    ) {
      toast.error(meetingCompletedBlockedMessage());
      return;
    }
    try {
      const payload = {
        ...form,
        lead_id: form.lead_id ? Number(form.lead_id) : null,
        student_id: form.student_id ? Number(form.student_id) : null,
        zoom_meeting_id: form.zoom_meeting_id || null,
        zoom_join_url: form.zoom_join_url || null,
        zoom_start_url: form.zoom_start_url || null,
        zoom_passcode: form.zoom_passcode || null
      };
      if (editingMeeting) {
        await meetingsAPI.update(editingMeeting.meeting_id, payload);
        toast.success(t('meetings.toasts.updated'));
      } else {
        const { status: _omitStatus, ...createPayload } = payload;
        await meetingsAPI.create(createPayload);
        toast.success(t('meetings.toasts.scheduled'));
      }
      setShowModal(false);
      setEditingMeeting(null);
      setInitialMeetingSlot(null);
      setForm(emptyForm);
      loadMeetings();
    } catch (err) { toast.error(err.response?.data?.message || t('meetings.toasts.failed')); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('meetings.deleteConfirm'))) return;
    try {
      await meetingsAPI.delete(id);
      toast.success(t('meetings.toasts.deleted'));
      loadMeetings();
    } catch (err) { toast.error(t('meetings.toasts.deleteFailed')); }
  };

  const handleEdit = (meeting) => {
    setEditingMeeting(meeting);
    const mt = meeting.meeting_time ? String(meeting.meeting_time).slice(0, 5) : '';
    setForm({
      title: meeting.title, meeting_type: meeting.meeting_type,
      meeting_date: meeting.meeting_date?.slice(0, 10) || '',
      meeting_time: mt,
      timezone: meeting.timezone || resolveDefaultTimezoneFromList(TIME_ZONES),
      mode: meeting.mode || 'Online', location: meeting.location || '',
      meeting_link: meeting.meeting_link || '',
      zoom_meeting_id: meeting.zoom_meeting_id || '',
      zoom_join_url: meeting.zoom_join_url || '',
      zoom_start_url: meeting.zoom_start_url || '',
      zoom_passcode: meeting.zoom_passcode || '',
      lead_id: meeting.lead_id != null ? String(meeting.lead_id) : '',
      student_id: meeting.student_id != null ? String(meeting.student_id) : '',
      notes: meeting.notes || '', status: meeting.status || 'Scheduled'
    });
    setInitialMeetingSlot({
      date: meeting.meeting_date?.slice(0, 10) || '',
      time: mt
    });
    setShowModal(true);
  };

  const handleStatusUpdate = async (meeting, newStatus) => {
    if (
      newStatus === 'Completed' &&
      meeting.status !== 'Completed' &&
      !canMarkMeetingCompleted(meeting, user?.role, activeCountry)
    ) {
      toast.error(meetingCompletedBlockedMessage());
      return;
    }
    try {
      setUpdatingStatusId(meeting.meeting_id);
      await meetingsAPI.update(meeting.meeting_id, { status: newStatus });
      toast.success(t('meetings.toasts.statusUpdated', { status: newStatus.toLowerCase() }));
      loadMeetings();
    } catch (err) {
      toast.error(err.response?.data?.message || t('meetings.toasts.statusUpdateFailed'));
    } finally { setUpdatingStatusId(null); }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Scheduled': 'var(--info)', 'Completed': 'var(--success)',
      'Cancelled': 'var(--danger)', 'Rescheduled': 'var(--warning)'
    };
    return colors[status] || 'var(--text-muted)';
  };

  const groupedByDate = filteredMeetings.reduce((acc, m) => {
    const date = m.meeting_date?.slice(0, 10) || 'Unknown';
    if (!acc[date]) acc[date] = [];
    acc[date].push(m);
    return acc;
  }, {});

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>{t('meetings.title')}</h2>
          <p className="page-subtitle">{t('meetings.meetingsFound', { count: clientTableFilterMode ? listTotalForPaging : pagination.total })}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <div className="view-toggle">
            <button className={viewMode === 'list' ? 'active' : ''} onClick={() => setViewMode('list')}>{t('meetings.listView')}</button>
            <button className={viewMode === 'calendar' ? 'active' : ''} onClick={() => setViewMode('calendar')}>{t('meetings.calendarView')}</button>
          </div>
          {canPerformAction('meetings', 'create') && <button className="btn btn-primary" onClick={() => { setEditingMeeting(null); setInitialMeetingSlot(null); setForm(emptyForm); setShowModal(true); }}>
            <FiPlus /> {t('meetings.scheduleBtn')}
          </button>}
        </div>
      </div>

      {/* Filters */}
      <div className="filter-bar">
        <div className="search-box">
          <FiSearch />
          <input placeholder={t('meetings.searchPlaceholder')} value={tableFilters.globalSearch}
            onChange={(e) => setTableFilters((prev) => ({ ...prev, globalSearch: e.target.value }))}
            onKeyDown={(e) => e.key === 'Enter' && loadMeetings()} />
        </div>
        <select value={filters.meeting_type} onChange={(e) => setFilters(f => ({ ...f, meeting_type: e.target.value }))}>
          <option value="">{t('meetings.allTypes')}</option>
          {MEETING_TYPES.map(mt => <option key={mt} value={mt}>{meetingTypeLabel(mt)}</option>)}
        </select>
        <select value={filters.status} onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}>
          <option value="">{t('meetings.allStatuses')}</option>
          {MEETING_STATUSES.map(s => <option key={s} value={s}>{meetingStatusLabel(s)}</option>)}
        </select>
        <input type="date" value={filters.date_from} onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))} />
        <input type="date" value={filters.date_to} onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value }))} />
        <button className="btn btn-outline btn-sm" onClick={() => { setFilters({ search: '', meeting_type: '', status: '', date_from: '', date_to: '' }); }}>
          <FiFilter /> {t('common.clear')}
        </button>
        <ColumnCustomizer columns={mtgColDefs} isVisible={isVisible} toggleColumn={toggleColumn} resetToDefault={resetToDefault} />
      </div>

      {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : (
        viewMode === 'list' ? (
          <div className="table-container">
            <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  {isVisible('title') && <UnifiedFilterHeaderCell column={columnFilterMap.title} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('meeting_type') && <UnifiedFilterHeaderCell column={columnFilterMap.meeting_type} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('date_time') && <UnifiedFilterHeaderCell column={columnFilterMap.date_time} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('mode') && <UnifiedFilterHeaderCell column={columnFilterMap.mode} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('location_link') && <UnifiedFilterHeaderCell column={columnFilterMap.location_link} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('created_by') && <UnifiedFilterHeaderCell column={columnFilterMap.created_by} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('status') && <UnifiedFilterHeaderCell column={columnFilterMap.status} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('lead_name') && <UnifiedFilterHeaderCell column={columnFilterMap.lead_name} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('student_full_name') && <UnifiedFilterHeaderCell column={columnFilterMap.student_full_name} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('notes') && <UnifiedFilterHeaderCell column={columnFilterMap.notes} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('outcome') && <UnifiedFilterHeaderCell column={columnFilterMap.outcome} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('next_action') && <UnifiedFilterHeaderCell column={columnFilterMap.next_action} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('lead_ref') && <UnifiedFilterHeaderCell column={columnFilterMap.lead_ref} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {pagedFilteredMeetings.map(m => (
                  <tr key={m.meeting_id}>
                    {isVisible('title') && <td><strong>{m.title}</strong>
                      {m.lead_name && (
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          <span style={{ fontWeight: 600 }}>{t('meetings.leadPrefix')}</span>
                          {m.lead_name}
                        </div>
                      )}
                      {m.student_full_name && (
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          <span style={{ fontWeight: 600 }}>{t('meetings.studentPrefix')}</span>
                          {m.student_full_name}
                        </div>
                      )}
                    </td>}
                    {isVisible('meeting_type') && <td><StatusBadge status={m.meeting_type} /></td>}
                    {isVisible('date_time') && <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <FiCalendar size={12} /> {new Date(m.meeting_date).toLocaleDateString('en-IN', { timeZone: countryConfig.defaultTimezone })}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-muted)' }}>
                        <FiClock size={12} /> {m.meeting_time}
                      </div>
                      {m.timezone && (
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          {t('meetings.timeZone')}: {m.timezone}
                        </div>
                      )}
                    </td>}
                    {isVisible('mode') && <td>{meetingModeLabel(m.mode)}</td>}
                    {isVisible('location_link') && <td>
                      {m.mode === 'Offline' && m.location ? <><FiMapPin size={12} /> {m.location}</> : null}
                      {m.mode === 'Online' && m.meeting_link ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <div><FiLink size={12} /> <a href={m.meeting_link} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: 'var(--primary)' }}>{t('meetings.joinMeeting')}</a></div>
                          {m.zoom_meeting_id && (
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                              {m.zoom_recording_url && (
                                <a href={m.zoom_recording_url} target="_blank" rel="noopener noreferrer"
                                  style={{ fontSize: 11, color: '#059669', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                  <FiPlay size={10} /> {t('meetings.recording')}
                                </a>
                              )}
                              {m.zoom_transcript_url && (
                                <a href={m.zoom_transcript_url} target="_blank" rel="noopener noreferrer"
                                  style={{ fontSize: 11, color: '#7c3aed', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                  <FiFileText size={10} /> {t('meetings.transcript')}
                                </a>
                              )}
                              {m.zoom_duration_minutes && (
                                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{m.zoom_duration_minutes} {t('meetings.min')}</span>
                              )}
                              <button
                                title={t('meetings.syncZoom')}
                                className="btn btn-sm btn-outline"
                                style={{ padding: '2px 6px', fontSize: 11 }}
                                disabled={zoomSyncing === m.meeting_id}
                                onClick={() => syncZoomData(m)}
                              >
                                <FiRefreshCw size={11} className={zoomSyncing === m.meeting_id ? 'spin' : ''} />
                              </button>
                            </div>
                          )}
                        </div>
                      ) : null}
                      {m.mode === 'Online' && !m.zoom_meeting_id && zoomConfigured && (
                        <button
                          className="btn btn-sm btn-outline"
                          style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                          disabled={zoomGenerating === m.meeting_id}
                          onClick={() => generateZoomLink(m)}
                        >
                          <FiVideo size={12} /> {zoomGenerating === m.meeting_id ? t('meetings.creatingZoom') : t('meetings.generateZoom')}
                        </button>
                      )}
                      {m.mode === 'Online' && !m.zoom_meeting_id && !zoomConfigured && '-'}
                      {m.mode === 'Offline' && !m.location && '-'}
                    </td>}
                    {isVisible('created_by') && <td>{m.created_by_name || '-'}</td>}
                    {isVisible('status') && <td>
                      <select
                        value={m.status || 'Scheduled'}
                        onChange={(e) => handleStatusSelectChange(m, e.target.value)}
                        disabled={updatingStatusId === m.meeting_id}
                        style={{
                          minWidth: 130,
                          height: 32,
                          fontSize: 12,
                          fontWeight: 600,
                          borderRadius: 6,
                          border: '1px solid var(--input-border)',
                          color: 'var(--text-primary)',
                          background: 'var(--bg-primary)',
                          padding: '0 8px'
                        }}
                      >
                        {MEETING_STATUSES.map(s => <option key={s} value={s}>{meetingStatusLabel(s)}</option>)}
                      </select>
                    </td>}
                    {isVisible('lead_name') && <td style={{ fontSize: 13 }}>{m.lead_name || '-'}</td>}
                    {isVisible('student_full_name') && <td style={{ fontSize: 13 }}>{m.student_full_name || '-'}</td>}
                    {isVisible('notes') && <td style={{ fontSize: 13, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.notes || '-'}</td>}
                    {isVisible('outcome') && <td style={{ fontSize: 13 }}>{m.outcome || '-'}</td>}
                    {isVisible('next_action') && <td style={{ fontSize: 13 }}>{m.next_action || '-'}</td>}
                    {isVisible('lead_ref') && <td style={{ fontSize: 13 }}>{m.lead_ref || '-'}</td>}
                    <td>
                      <div className="table-actions">
                        <button className="edit" title={t('common.edit')} onClick={() => handleEdit(m)}><FiEdit /></button>
                        <button className="delete" title={t('common.delete')} onClick={() => handleDelete(m.meeting_id)}><FiTrash2 /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredMeetings.length === 0 && <tr><td colSpan={100} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>{t('meetings.noMeetings')}</td></tr>}
              </tbody>
            </table>
            </div>
          </div>
        ) : (
          /* Calendar view */
          <div>
            {Object.entries(groupedByDate).sort(([a], [b]) => a.localeCompare(b)).map(([date, items]) => (
              <div key={date} style={{ marginBottom: 20 }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--primary)', marginBottom: 8, borderBottom: '2px solid var(--primary-lighter)', paddingBottom: 6 }}>
                  <FiCalendar style={{ marginRight: 6 }} />
                  {new Date(date).toLocaleDateString('en-IN', { timeZone: countryConfig.defaultTimezone, weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </h3>
                <div style={{ display: 'grid', gap: 8 }}>
                  {items.map(m => (
                    <div key={m.meeting_id} className="card" style={{ padding: '12px 16px', borderLeft: `4px solid ${getStatusColor(m.status)}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                        <div style={{ minWidth: 0 }}>
                          <strong>{m.title}</strong>
                          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                            <FiClock size={12} /> {m.meeting_time} {m.timezone ? `(${m.timezone})` : ''} &bull; {meetingModeLabel(m.mode)}
                            {m.location && <> &bull; <FiMapPin size={12} /> {m.location}</>}
                            {m.meeting_link && <> &bull; <FiLink size={12} /> <a href={m.meeting_link} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>{t('meetings.join')}</a></>}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                          <span style={{
                            padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                            color: 'var(--white)', background: getStatusColor(m.status)
                          }}>{meetingStatusLabel(m.status)}</span>
                          <button className="btn btn-sm btn-outline" onClick={() => handleEdit(m)}><FiEdit size={12} /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {Object.keys(groupedByDate).length === 0 && <div className="empty-state"><p>{t('meetings.noMeetings')}</p></div>}
          </div>
        )
      )}

      {/* Pagination */}
      {(clientTableFilterMode ? effectiveTotalPages : pagination.pages) > 1 && (
        <div className="pagination">
          <button disabled={pagination.page <= 1} onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}>{t('common.previous')}</button>
          <span>{t('common.page', { current: pagination.page, total: clientTableFilterMode ? effectiveTotalPages : pagination.pages })}</span>
          <button
            disabled={pagination.page >= (clientTableFilterMode ? effectiveTotalPages : pagination.pages)}
            onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
          >
            {t('common.next')}
          </button>
        </div>
      )}

      {/* Meeting Form Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setMeetingHistory([]);
          setMeetingHistoryLoading(false);
        }}
        title={editingMeeting ? t('meetings.editTitle') : t('meetings.scheduleTitle')}
        size="lg"
        footer={<><button className="btn btn-outline" onClick={() => setShowModal(false)}>{t('common.cancel')}</button>
        <button className="btn btn-primary" onClick={handleSubmit}>{editingMeeting ? t('common.update') : t('common.schedule')}</button></>}>
        <div className="form-grid">
          <div className="form-group full-width">
            <label>{t('meetings.form.title')} <span className="required">*</span></label>
            <input value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} placeholder={t('meetings.form.titlePlaceholder')} />
          </div>
          <div className="form-group full-width">
            <label>{t('meetings.form.b2bPartner')}</label>
            <select
              value={form.lead_id === '' || form.lead_id == null ? '' : String(form.lead_id)}
              onChange={(e) => setForm((f) => ({ ...f, lead_id: e.target.value }))}
            >
              <option value="">{t('meetings.form.noAgency')}</option>
              {b2bPartners.map((a) => (
                <option key={a.lead_id} value={a.lead_id}>
                  {leadOptionLabel(a)}
                </option>
              ))}
            </select>
            {b2bPartners.length === 0 && (
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '6px 0 0' }}>
                {t('meetings.form.noAgenciesHint')}
              </p>
            )}
          </div>
          <div className="form-group">
            <label>{t('meetings.form.meetingType')} <span className="required">*</span></label>
            <select value={form.meeting_type} onChange={(e) => setForm(f => ({ ...f, meeting_type: e.target.value }))}>
              {MEETING_TYPES.map(mt => <option key={mt} value={mt}>{meetingTypeLabel(mt)}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>{t('meetings.form.mode')} <span className="required">*</span></label>
            <select value={form.mode} onChange={(e) => setForm(f => ({ ...f, mode: e.target.value }))}>
              {MEETING_MODES.map(md => <option key={md} value={md}>{meetingModeLabel(md)}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>{t('meetings.form.date')} <span className="required">*</span></label>
            <input
              type="date"
              value={form.meeting_date}
              onChange={(e) => setForm(f => ({ ...f, meeting_date: e.target.value }))}
              min={editingMeeting ? undefined : getTodayDateInputMin()}
            />
          </div>
          <div className="form-group">
            <label>{t('meetings.form.time')} <span className="required">*</span></label>
            <input type="time" value={form.meeting_time} onChange={(e) => setForm(f => ({ ...f, meeting_time: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('meetings.form.timezone')} <span className="required">*</span></label>
            <select value={form.timezone || resolveDefaultTimezoneFromList(TIME_ZONES)} onChange={(e) => setForm(f => ({ ...f, timezone: e.target.value }))}>
              {TIME_ZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </div>
          {form.mode === 'Offline' && (
            <div className="form-group">
              <label>{t('meetings.form.location')} <span className="required">*</span></label>
              <input value={form.location} onChange={(e) => setForm(f => ({ ...f, location: e.target.value }))} placeholder={t('meetings.form.locationPlaceholder')} />
            </div>
          )}
          {form.mode === 'Online' && (
            <div className="form-group full-width">
              <label>{t('meetings.form.meetingLink')}</label>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 8px' }}>
                {t('meetings.form.meetingLinkHint')}
              </p>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  style={{ flex: 1, minWidth: 200 }}
                  value={form.meeting_link}
                  onChange={(e) => setForm(f => ({ ...f, meeting_link: e.target.value }))}
                  placeholder={t('meetings.linkPlaceholder')}
                />
                {zoomConfigured && !form.zoom_meeting_id && (
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    disabled={zoomGeneratingModal}
                    onClick={generateZoomInModal}
                  >
                    <FiVideo size={14} /> {zoomGeneratingModal ? t('meetings.creatingZoom') : t('meetings.generateZoom')}
                  </button>
                )}
              </div>
              {form.zoom_meeting_id && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                  {t('meetings.zoomMeetingId')}: {form.zoom_meeting_id}
                  {form.zoom_passcode ? ` · ${t('meetings.zoomPasscode')}: ${form.zoom_passcode}` : ''}
                </div>
              )}
            </div>
          )}
          {editingMeeting && (
            <div className="form-group">
              <label>{t('meetings.form.status')}</label>
              <select value={form.status} onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))}>
                {MEETING_STATUSES.map(s => <option key={s} value={s}>{meetingStatusLabel(s)}</option>)}
              </select>
            </div>
          )}
          <div className="form-group full-width">
            <label>{t('meetings.form.notes')}</label>
            <textarea value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} placeholder={t('meetings.form.notesPlaceholder')} />
          </div>
        </div>

        {editingMeeting && (
          <div style={{ marginTop: 14 }}>
            <h3 style={{ margin: '8px 0 10px', fontWeight: 800 }}>{t('common.history')}</h3>
            {meetingHistoryLoading ? (
              <div className="loading-spinner"><div className="spinner" /></div>
            ) : (
              <AuditTimeline logs={meetingHistory} showEntity={false} />
            )}
          </div>
        )}
      </Modal>

      {/* Reschedule: opened when status -> Rescheduled from list */}
      <Modal
        isOpen={showRescheduleModal && !!rescheduleForm && !!rescheduleMeeting}
        onClose={closeRescheduleModal}
        title={t('meetings.rescheduleTitle')}
        size="lg"
        footer={
          <>
            <button type="button" className="btn btn-outline" onClick={closeRescheduleModal}>{t('common.cancel')}</button>
            <button type="button" className="btn btn-primary" onClick={handleRescheduleSubmit} disabled={updatingStatusId === rescheduleMeeting?.meeting_id}>
              {updatingStatusId === rescheduleMeeting?.meeting_id ? t('common.saving') : t('meetings.saveReschedule')}
            </button>
          </>
        }
      >
        {rescheduleMeeting && rescheduleForm && (
          <div className="form-grid">
            <div className="form-group full-width">
              <label>{t('meetings.form.meeting')}</label>
              <p style={{ margin: 0, fontWeight: 600 }}>{rescheduleMeeting.title}</p>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '6px 0 0' }}>
                {t('meetings.rescheduleHint')}
              </p>
            </div>
            <div className="form-group">
              <label>{t('meetings.form.newDate')} <span className="required">*</span></label>
              <input
                type="date"
                value={rescheduleForm.meeting_date}
                onChange={(e) => setRescheduleForm((f) => ({ ...f, meeting_date: e.target.value }))}
                min={getTodayDateInputMin()}
              />
            </div>
            <div className="form-group">
              <label>{t('meetings.form.newTime')} <span className="required">*</span></label>
              <input
                type="time"
                value={rescheduleForm.meeting_time}
                onChange={(e) => setRescheduleForm((f) => ({ ...f, meeting_time: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>{t('meetings.form.timezone')} <span className="required">*</span></label>
              <select
                value={rescheduleForm.timezone || resolveDefaultTimezoneFromList(TIME_ZONES)}
                onChange={(e) => setRescheduleForm((f) => ({ ...f, timezone: e.target.value }))}
              >
                {TIME_ZONES.map((tz) => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>{t('meetings.form.mode')}</label>
              <select
                value={rescheduleForm.mode}
                onChange={(e) => setRescheduleForm((f) => ({ ...f, mode: e.target.value }))}
              >
                {MEETING_MODES.map((md) => (
                  <option key={md} value={md}>{meetingModeLabel(md)}</option>
                ))}
              </select>
            </div>
            {rescheduleForm.mode === 'Offline' && (
              <div className="form-group full-width">
                <label>{t('meetings.form.location')} <span className="required">*</span></label>
                <input
                  value={rescheduleForm.location}
                  onChange={(e) => setRescheduleForm((f) => ({ ...f, location: e.target.value }))}
                  placeholder={t('meetings.form.locationPlaceholder')}
                />
              </div>
            )}
            {rescheduleForm.mode === 'Online' && (
              <div className="form-group full-width">
                <label>{t('meetings.form.meetingLink')}</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    style={{ flex: 1, minWidth: 200 }}
                    value={rescheduleForm.meeting_link}
                    onChange={(e) => setRescheduleForm((f) => ({ ...f, meeting_link: e.target.value }))}
                    placeholder={t('meetings.linkPlaceholder')}
                  />
                  {zoomConfigured && (
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                      disabled={zoomGeneratingReschedule}
                      onClick={generateZoomInRescheduleModal}
                    >
                      <FiVideo size={14} /> {zoomGeneratingReschedule ? t('meetings.workingZoom') : t('meetings.generateZoom')}
                    </button>
                  )}
                </div>
                {rescheduleForm.zoom_meeting_id && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                    {t('meetings.zoomMeetingId')}: {rescheduleForm.zoom_meeting_id}
                    {rescheduleForm.zoom_passcode ? ` · ${t('meetings.zoomPasscode')}: ${rescheduleForm.zoom_passcode}` : ''}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MeetingList;
